<script setup lang="ts">
import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth.ts'
import { storeToRefs } from 'pinia'
import IconChevronDown from '@/components/icons/IconChevronDown.vue'
const authStore = useAuthStore()
const { currentUser } = storeToRefs(authStore)
const menus = [
  {
    label: 'Logout',
    click: () => {
      authStore.signOut()
    },
  },
]
const menu = ref(false)
const imageLoadError = ref(false)
const handleImageError = (): void => {
  imageLoadError.value = true
}

const getInitials = (name: string): string => {
  if (!name || name.length === 0) {
    return ''
  }
  return name.charAt(0).toUpperCase()
}
</script>

<template>
  <v-menu :close-on-content-click="false" min-width="200px" v-model="menu">
    <template v-slot:activator="{ props }">
      <div
        v-bind="props"
        role="button"
        tabindex="0"
        aria-label="Account menu"
        class="d-flex align-center ga-2"
      >
        <img
          v-if="currentUser?.picture && !imageLoadError"
          alt="avatar"
          :src="currentUser.picture"
          class="tw:w-[24px] tw:h-[24px] tw:rounded-full"
          @error="handleImageError"
          referrerpolicy="no-referrer"
        />
        <div v-if="!currentUser?.picture || imageLoadError" class="">
          <v-avatar
            size="24px"
            class="tw:relative tw:z-10 tw:border tw:border-s-d-s-dark-border-brand-default tw:shrink-0 tw:bg-s-d-s-dark-background-disabled-default"
          >
            <span class="tw:font-medium tw:text-2">{{ getInitials(currentUser?.name ?? '') }}</span>
          </v-avatar>
        </div>
      </div>
    </template>
    <v-card>
      <div class="profile-container">
        <div class="profile">
          <img
            v-if="currentUser?.picture && !imageLoadError"
            alt="avatar"
            :src="currentUser.picture"
            class="profile-avatar"
            @error="handleImageError"
            referrerpolicy="no-referrer"
          />
          <div
            v-if="!currentUser?.picture || imageLoadError"
            class="d-flex align-center justify-center"
          >
            <v-avatar
              size="64px"
              class="tw:relative tw:z-10 tw:border tw:border-s-d-s-dark-border-brand-default tw:shrink-0 tw:bg-s-d-s-dark-background-disabled-default"
            >
              <span class="tw:font-medium tw:text-2">{{
                getInitials(currentUser?.name ?? '')
              }}</span>
            </v-avatar>
          </div>
          <div class="profile-info">
            <div class="text-subheading-strong dots-line w-100">
              {{ currentUser?.name }}
            </div>
            <span
              class="text-body-small dots-line w-100"
              style="color: var(--sds-color-text-default-tertiary)"
            >
              {{ currentUser?.email }}
              <v-tooltip v-if="currentUser?.email" activator="parent" location="top">{{
                currentUser?.email
              }}</v-tooltip>
            </span>
          </div>
        </div>
        <v-divider style="width: 100%"></v-divider>
        <div class="menu-item-container">
          <v-list-item
            variant="text"
            style="width: 100%"
            rounded
            v-for="menu in menus"
            :key="menu.label"
            @click="menu.click"
          >
            <div class="menu-item clickable">
              <div class="text-single-line-body-small-strong">{{ menu.label }}</div>
              <IconChevronDown direction="right" />
            </div>
          </v-list-item>
        </div>
        <!-- <v-btn variant="text" rounded @click="authStore.signOut"> Logout </v-btn> -->
      </div>
    </v-card>
  </v-menu>
</template>

<style scoped>
.profile {
  display: flex;
  align-items: center;
  gap: var(--sds-size-space-12);
  justify-content: center;
}
.profile-avatar {
  width: 72px;
  height: 72px;
  aspect-ratio: 1/1;
  border-radius: var(--sds-size-radius-100);
}
.profile-info {
  display: flex;
  flex-direction: column;
  gap: var(--sds-size-space-2);
  width: 100%;
  max-width: 150px;
}
.profile-container {
  display: flex;
  flex-direction: column;
  width: 280px;
  padding: var(--sds-size-space-16);
  gap: var(--sds-size-space-16);
}
.menu-item {
  display: flex;
  height: 30px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  cursor: pointer;
}
.menu-item-container {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--sds-size-space-4);
}
.user-avatar {
  display: flex;
  padding: var(--sds-size-space-8);
  justify-content: center;
  align-items: center;
  width: 38px;
  gap: 10px;
  border: 1px solid var(--sds-color-border-default-primary);
  background: var(--sds-color-background-surface-secondary);
}
</style>
